import { Link } from "react-router-dom";
import { X, Plus, Minus, ShoppingBag, Trash2, Scissors } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { formatGBP } from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";
import { FreeDeliveryBar } from "@/components/FreeDeliveryBar";
import { CustomFitSummary } from "@/components/CustomiseFit";

export const CartDrawer = () => {
  const { cart, drawerOpen, setDrawerOpen, updateItem, removeItem } = useCart();
  const { format } = useCurrency();
  const customFeeTotal = (cart?.items || []).reduce((s, it) => s + (it.custom_fee || 0), 0);

  return (
    <>
      <div
        className={`fixed inset-0 z-[70] bg-black/50 transition-opacity duration-300 ${
          drawerOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
        onClick={() => setDrawerOpen(false)}
      />
      <aside
        className={`fixed top-0 right-0 z-[80] h-full w-full max-w-md bg-white flex flex-col transition-transform duration-400 ease-out ${
          drawerOpen ? "translate-x-0" : "translate-x-full"
        }`}
        data-testid="cart-drawer"
      >
        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100">
          <h2 className="font-serif text-2xl flex items-center gap-2">
            <ShoppingBag className="w-5 h-5 text-pink" /> Your Bag
            <span className="font-sans text-sm text-gray-400">({cart.count})</span>
          </h2>
          <button onClick={() => setDrawerOpen(false)} aria-label="Close cart" data-testid="close-cart">
            <X className="w-6 h-6" />
          </button>
        </div>

        {(cart?.items?.length ?? 0) === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center gap-4 px-6 text-center">
            <ShoppingBag className="w-12 h-12 text-gray-200" />
            <p className="text-gray-500 font-sans">Your bag is empty.</p>
            <button
              onClick={() => setDrawerOpen(false)}
              className="bg-pink text-white px-8 py-3 text-xs uppercase tracking-[0.15em] hover:bg-pink-hover transition-colors"
            >
              Continue Shopping
            </button>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-5">
              {cart.items.map((item) => (
                <div key={item.line_id} className="flex gap-4" data-testid={`cart-item-${item.slug}`}>
                  <img src={item.image} alt={item.name} className="w-20 h-24 object-cover bg-surface" />
                  <div className="flex-1">
                    <div className="flex justify-between gap-2">
                      <div>
                        <h4 className="font-serif text-base leading-tight">{item.name}</h4>
                        <p className="text-xs text-gray-500">{item.subtitle}</p>
                        {(item.colour || item.size) && (
                          <p className="text-xs text-gray-400 mt-0.5">{[item.colour, item.size].filter(Boolean).join(" · ")}</p>
                        )}
                        <CustomFitSummary customization={item.customization} compact />
                      </div>
                      <button onClick={() => removeItem(item.line_id)} aria-label="Remove" data-testid={`remove-${item.slug}`}>
                        <Trash2 className="w-4 h-4 text-gray-400 hover:text-pink transition-colors" />
                      </button>
                    </div>
                    <div className="flex items-center justify-between mt-3">
                      <div className="flex items-center border border-gray-200">
                        <button
                          onClick={() => updateItem(item.line_id, item.quantity - 1)}
                          className="px-2 py-1.5 hover:text-pink"
                          data-testid={`decrease-${item.slug}`}
                        >
                          <Minus className="w-3.5 h-3.5" />
                        </button>
                        <span className="px-3 text-sm">{item.quantity}</span>
                        <button
                          onClick={() => updateItem(item.line_id, item.quantity + 1)}
                          className="px-2 py-1.5 hover:text-pink"
                          data-testid={`increase-${item.slug}`}
                        >
                          <Plus className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      <span className="text-sm font-medium">{format(item.line_total)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="border-t border-gray-100 px-6 py-5 space-y-4">
              <FreeDeliveryBar subtotal={cart.subtotal} />
              {customFeeTotal > 0 && (
                <>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Items</span>
                    <span>{format(cart.subtotal - customFeeTotal)}</span>
                  </div>
                  <div className="flex justify-between text-sm" data-testid="drawer-tailoring-line">
                    <span className="text-gray-500 flex items-center gap-1.5"><Scissors className="w-3.5 h-3.5 text-gold" /> Custom tailoring</span>
                    <span>+{format(customFeeTotal)}</span>
                  </div>
                </>
              )}
              <div className="flex justify-between text-sm">
                <span className="text-gray-500 uppercase tracking-wide">Subtotal</span>
                <span className="font-medium text-lg">{format(cart.subtotal)}</span>
              </div>
              <Link
                to="/checkout"
                onClick={() => setDrawerOpen(false)}
                data-testid="drawer-checkout-btn"
                className="block w-full bg-pink text-white text-center py-4 rounded-full text-sm font-medium shadow-sm hover:bg-pink-hover hover:shadow-md transition-all"
              >
                Proceed to checkout
              </Link>
              <Link
                to="/cart"
                onClick={() => setDrawerOpen(false)}
                className="block w-full text-center py-2 text-xs uppercase tracking-[0.15em] text-charcoal hover:text-pink transition-colors"
              >
                View Bag
              </Link>
            </div>
          </>
        )}
      </aside>
    </>
  );
};
